import { FC, useState } from 'react';

import {
  GiftCouponForRedemption,
  GiftCouponRedeemStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import {
  formatDateTime,
  formatPhone,
  getChoiceUnitName,
  getItemName,
} from '../../utils/couponSearch.utils';

interface LaterReceiveModalProps {
  coupon: GiftCouponForRedemption;
  isProcessing: boolean;
  /** 확인 — 전환(또는 복구) 성공 시 true */
  onConfirm: (reason: string) => Promise<boolean>;
  onClose: () => void;
}

/**
 * 신청됨(Requested) ↔ 나중에 받기(Available + 수신번호) 전환 모달.
 *
 * 수령자가 "지금은 못 쓴다, 나중에 받겠다"고 연락해 온 건을 발송하지 않고 예약 상태로
 * 되돌린다. 수신번호·인증·서명은 지우지 않으므로 수령자는 받았던 링크로 다시 신청할 수 있다.
 */
export const LaterReceiveModal: FC<LaterReceiveModalProps> = ({
  coupon,
  isProcessing,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState('');
  const isRestore = coupon.redeemStatus === GiftCouponRedeemStatus.Available;
  const choiceUnitName = getChoiceUnitName(coupon);

  // 전환하면 이 번호가 회수 잠금(확인번호)으로 고정돼 다른 번호는 인증조차 안 된다.
  const lockPhone = (coupon.recipient?.phoneNumber ?? '').replace(/\D/g, '');
  const currentLock = (coupon.additionalRecipientPhone ?? '').replace(/\D/g, '');
  const replacesLock = !!currentLock && !!lockPhone && currentLock !== lockPhone;

  const handleConfirm = async () => {
    if (isProcessing) return;
    const ok = await onConfirm(reason);
    if (ok) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={isProcessing ? undefined : onClose}
    >
      <div
        className="mx-4 w-full max-w-md rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h3 className="text-lg font-bold text-gray-800">
            {isRestore ? '발송대기로 되돌리기' : '나중에 받기로 전환'}
          </h3>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          {/* 대상 쿠폰 */}
          <div className="space-y-1.5 rounded-lg bg-gray-50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">쿠폰번호</span>
              <span className="font-mono text-sm font-medium text-gray-800">
                {coupon.fullCode?.replace(/-undefined/g, '') || coupon.code}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="shrink-0 text-xs text-gray-500">상품</span>
              <span className="truncate text-sm font-medium text-gray-800">
                {getItemName(coupon)}
                {choiceUnitName && (
                  <span className="ml-1 text-xs text-indigo-600">{choiceUnitName}</span>
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">수신번호</span>
              <span className="font-mono text-sm font-medium text-gray-800">
                {formatPhone(coupon.recipient?.phoneNumber)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">신청일시</span>
              <span className="text-sm text-gray-700">
                {formatDateTime(coupon.recipient?.requestedAt)}
              </span>
            </div>
          </div>

          {/* 무슨 일이 일어나는지 */}
          {isRestore ? (
            <ul className="space-y-1 rounded-lg bg-yellow-50 p-3 text-xs leading-relaxed text-yellow-800">
              <li>· 이 쿠폰이 다시 발송대기(신청됨) 목록에 올라갑니다.</li>
              <li>· 다음 발송 회차에 그대로 발송 대상이 됩니다.</li>
              {!!currentLock && (
                <li>
                  · 고정된 수신번호({formatPhone(currentLock)})는 그대로 유지됩니다 —
                  바꾸려면 표의 확인번호를 수정하세요.
                </li>
              )}
            </ul>
          ) : (
            <ul className="space-y-1 rounded-lg bg-purple-50 p-3 text-xs leading-relaxed text-purple-800">
              <li>· 발송대기 목록에서 빠져 기프티콘이 발송되지 않습니다.</li>
              <li>
                · 수신번호·본인인증·서명은 그대로 남아, 수령자가 받았던 링크로 다시
                신청하면 그때 발송하면 됩니다.
              </li>
              {lockPhone ? (
                <li className="font-semibold">
                  · 수신번호 {formatPhone(lockPhone)}로 <b>고정</b>됩니다 — 다른 번호로는
                  인증번호 발송·신청이 차단됩니다.
                  {replacesLock && (
                    <span className="mt-0.5 block font-normal text-rose-700">
                      기존 확인번호 {formatPhone(currentLock)} → {formatPhone(lockPhone)}{' '}
                      로 교체됩니다.
                    </span>
                  )}
                </li>
              ) : (
                <li className="font-semibold text-rose-700">
                  · 수신번호가 없어 번호 고정을 할 수 없습니다 — 링크를 받은 누구나 신청할
                  수 있으니, 확인번호를 직접 채워 두세요.
                </li>
              )}
              <li>· 수령자에게 안내 문자가 자동으로 나가지는 않습니다.</li>
              <li>· 유효기간(만료일)은 그대로이니 기한 안내를 함께 해주세요.</li>
            </ul>
          )}

          {/* 사유 — CS 응대 메모로 */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              사유 (선택) — 입력하면 이 쿠폰의 CS 응대 메모로 남습니다
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isProcessing}
              rows={2}
              placeholder={
                isRestore
                  ? '예) 잘못 전환해서 되돌림'
                  : '예) 미국 체류 중이라 지금은 못 쓴다고 연락 옴'
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50"
            />
          </div>
        </div>

        <div className="flex gap-2 border-t px-5 py-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={isProcessing}
            className={`flex-1 rounded-lg py-2.5 text-sm font-medium text-white transition disabled:opacity-50 ${
              isRestore
                ? 'bg-yellow-600 hover:bg-yellow-700'
                : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            {isProcessing
              ? '처리 중...'
              : isRestore
              ? '발송대기로 되돌리기'
              : '나중에 받기로 전환'}
          </button>
        </div>
      </div>
    </div>
  );
};
